import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from '@/components/ui/input-otp';
import { Button } from '@/components/ui/button';
import { useInvitationAuthentication } from '@/hooks/useInvitationAuthentication';
import BlignyLogo from '@/assets/BlignyLogo.png';
import { formatPracticeWelcome } from '@/util/practice';
import { isDesktopDevice } from '@/util/device';
import { SecureServiceMessage } from '@/components/SecureService';

export const InvitationAuthentication = () => {
  const {
    professional,
    practice,
    lastName,
    error,
    isComposing,
    handleChange,
    handleCompositionStart,
    handleCompositionEnd,
    showFallback,
    handleFallback,
  } = useInvitationAuthentication();

  const autoFocus = isDesktopDevice();

  return (
    <>
      {/* Fond plein écran sous le header, avec opacité pour effet sombre */}
      <div
        className="fixed inset-x-0 bottom-0 top-20 z-0 bg-indigo-800/45"
        aria-hidden
      />
      <div className="relative z-10 h-full flex flex-col items-center px-4 n2xs:px-6 nsm:px-0">
        <div className="flex-1 flex flex-col items-center justify-start pt-6 nsm:pt-0 max-w-sm gap-2">
          {/* Header with professional/practice info */}
          <div className="flex flex-col items-center mb-8">
            {professional ? (
              // TODO: we need to have a dynamic logo stored in the practice model
              practice?.name.includes('Bligny') ? (
                <img src={BlignyLogo} alt="Bligny Logo" className="w-50 mb-4" />
              ) : practice?.onboardingDisplayMode === 'PRACTICE' &&
                practice?.name ? (
                <p className="text-xl font-light text-center mb-4">
                  {formatPracticeWelcome(practice)}
                </p>
              ) : (
                <></>
              )
            ) : (
              <p className="text-xl font-light text-center mb-4">Bienvenue</p>
            )}
          </div>

          <SecureServiceMessage />

          <div className="flex flex-col items-center w-full p-6 rounded-xl border border-gray-200 shadow-md bg-white">
            <div className="flex flex-col items-center mb-6">
              <div className="flex items-center gap-2 mb-1">
                <p className="text-xl font-medium text-center">
                  Entrez les 4 premières lettres <br /> du nom de famille du
                  patient :
                </p>
              </div>
            </div>

            <div className="flex flex-col items-center gap-4 w-full">
              <div
                className={`flex flex-col items-center rounded-md ${
                  error ? 'bg-red-100 gap-1 p-2' : 'bg-transparent'
                }`}
              >
                <div className="flex gap-2 justify-center">
                  <InputOTP
                    autoFocus={autoFocus}
                    maxLength={isComposing ? 5 : 4}
                    onCompositionStart={handleCompositionStart}
                    onCompositionEnd={handleCompositionEnd}
                    value={lastName}
                    onChange={handleChange}
                    disabled={error === 'error' || error === 'too-many-tries'}
                    autoComplete="off"
                    className={`flex gap-2 justify-center [&_input]:focus:outline-none [&_input]:focus:ring-0 ${
                      error ? 'bg-red-25 border-red-300' : 'border-defaultColor'
                    }`}
                  >
                    <InputOTPGroup className="flex gap-2">
                      <InputOTPSlot
                        index={0}
                        className={`w-11.5 h-13 border border-gray-200 rounded-sm text-center uppercase text-xl focus:outline-none focus:ring-0 data-[active=true]:bg-blue-50 focus:border-defaultColor data-[active=true]:border-defaultColor ${
                          lastName[0]
                            ? 'bg-blue-50 border-defaultColor'
                            : 'bg-white'
                        }`}
                      />
                      <InputOTPSlot
                        index={1}
                        className={`w-11.5 h-13 border border-gray-200 rounded-sm text-center uppercase text-xl focus:outline-none focus:ring-0 data-[active=true]:bg-blue-50 focus:border-defaultColor data-[active=true]:border-defaultColor ${
                          lastName[1]
                            ? 'bg-blue-50 border-defaultColor'
                            : 'bg-white'
                        }`}
                      />
                      <InputOTPSlot
                        index={2}
                        className={`w-11.5 h-13 border border-gray-200 rounded-sm text-center uppercase text-xl focus:outline-none focus:ring-0 data-[active=true]:bg-blue-50 focus:border-defaultColor data-[active=true]:border-defaultColor ${
                          lastName[2]
                            ? 'bg-blue-50 border-defaultColor'
                            : 'bg-white'
                        }`}
                      />
                      <InputOTPSlot
                        index={3}
                        className={`w-11.5 h-13 border border-gray-200 rounded-sm text-center uppercase text-xl focus:outline-none focus:ring-0 data-[active=true]:bg-blue-50 focus:border-defaultColor data-[active=true]:border-defaultColor ${
                          lastName[3]
                            ? 'bg-blue-50 border-defaultColor'
                            : 'bg-white'
                        }`}
                      />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
                {error && (
                  <p className="w-52 text-left text-red-600 text-xs">
                    {error === 'error' && 'Une erreur est survenue.'}
                    {error === 'too-many-tries' &&
                      'Vous avez effectué trop de tentatives.'}
                    {error === 'fail' &&
                      'Les informations renseignées ne correspondent pas.'}
                  </p>
                )}
              </div>
              {/* Fallback button */}
              {showFallback && (
                <div className="pt-2 border-t border-gray-100 w-full flex justify-center">
                  <Button
                    variant="ghost"
                    size="md"
                    onClick={handleFallback}
                    className="text-sm font-normal underline"
                  >
                    Continuer sans s&apos;identifier
                  </Button>
                </div>
              )}

              {/* Examples */}
              <div
                className={`w-full flex flex-col items-center gap-1 text-sm font-light text-[#848282] ${
                  showFallback ? '' : 'mt-2'
                }`}
              >
                <p>
                  Marie <span className="font-medium">Curi</span>e :{' '}
                  <span className="font-medium">CURI</span>
                </p>
                <p>
                  François <span className="font-medium">Le Dr</span>an :{' '}
                  <span className="font-medium">LEDR</span>
                </p>
                <p>
                  Charles <span className="font-medium">Kao</span> :{' '}
                  <span className="font-medium">KAO</span>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
